import {
  BRIDGE_MESSAGE_SOURCES,
  BRIDGE_MESSAGE_TARGETS,
  createBridgeEnvelope,
} from "@bezoned/desktop-bridge/bridge-common.js";
import { DESKTOP_SYNC_MESSAGE_TYPES } from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import type { DesktopSyncChatSection } from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useChatState } from "../chat/chat-state.provider.js";
import { simpleStringHash } from "./desktop-bridge-sync.utils.js";
import { useDesktopBridgeRoom } from "./use-desktop-bridge-room.js";
import { useDesktopBridge } from "./use-desktop-bridge.js";

const toChatThread = (
  credentials: {
    acsThreadId: string | null;
    topic?: string | null;
    endpoint?: string | null;
    acsUserId?: string | null;
    token?: string | null;
  } | null,
  topic?: string | null,
  isLoading?: boolean
) => {
  if (!credentials?.acsThreadId) {
    return null;
  }

  return {
    acsThreadId: credentials.acsThreadId,
    acsUserId: credentials?.acsUserId ?? null,
    endpoint: credentials?.endpoint ?? null,
    isLoading: isLoading ?? false,
    token: credentials?.token ?? null,
    topic: topic ?? credentials?.topic ?? null,
  };
};

export const DesktopBridgeChatSyncListener = () => {
  const {
    officeThreadId,
    officeCredentials,
    officeRefreshToken,
    tableCredentials,
    tableChatThreadId,
    tableTopic,
    tableRefreshToken,
    isLoadingTableChat,
  } = useChatState();
  const { status: bridgeStatus, instanceId, sendJson } = useDesktopBridge();
  const bridgeRoom = useDesktopBridgeRoom();

  const lastChatSignatureRef = useRef<string | null>(null);
  const didRefreshCredentialsForBridgeRef = useRef(false);

  const chatSection: DesktopSyncChatSection = useMemo(
    () => ({
      office: toChatThread(officeCredentials),
      table: toChatThread(tableCredentials, tableTopic, isLoadingTableChat),
    }),
    [officeCredentials, tableCredentials, tableTopic, isLoadingTableChat]
  );

  const sendChatUpdate = useCallback(
    (chat: DesktopSyncChatSection) => {
      if (!(bridgeStatus === "ready" && instanceId)) {
        return;
      }

      const signature = String(simpleStringHash(JSON.stringify(chat)));
      if (lastChatSignatureRef.current === signature) {
        return;
      }
      lastChatSignatureRef.current = signature;

      sendJson(
        bridgeRoom,
        createBridgeEnvelope({
          instanceId,
          payload: {
            sections: {
              chat,
            },
          },
          source: BRIDGE_MESSAGE_SOURCES.FRONTEND,
          target: BRIDGE_MESSAGE_TARGETS.DESKTOP,
          type: DESKTOP_SYNC_MESSAGE_TYPES.CHAT_UPDATE,
        })
      );
    },
    [bridgeRoom, bridgeStatus, instanceId, sendJson]
  );

  useEffect(() => {
    if (bridgeStatus !== "ready") {
      lastChatSignatureRef.current = null;
      didRefreshCredentialsForBridgeRef.current = false;
      return;
    }

    sendChatUpdate(chatSection);
  }, [bridgeStatus, chatSection, sendChatUpdate]);

  useEffect(() => {
    if (bridgeStatus !== "ready") {
      return;
    }

    if (didRefreshCredentialsForBridgeRef.current) {
      return;
    }

    didRefreshCredentialsForBridgeRef.current = true;

    const refreshTasks: Promise<{ token: string; expiresOn: Date }>[] = [];
    if (officeThreadId && officeRefreshToken) {
      refreshTasks.push(officeRefreshToken());
    }
    if (tableChatThreadId && tableRefreshToken) {
      refreshTasks.push(tableRefreshToken());
    }

    if (refreshTasks.length === 0) {
      return;
    }

    Promise.allSettled(refreshTasks);
  }, [
    bridgeStatus,
    officeThreadId,
    officeRefreshToken,
    tableChatThreadId,
    tableRefreshToken,
  ]);

  return null;
};
